import React from "react";
import { Box, Typography, Switch, FormControlLabel, Divider } from "@mui/material";
import { DarkMode, LightMode, FeedbackOutlined } from "@mui/icons-material";
import { useTheme } from "../../../context/ThemeContext";
import { useBackground, BackgroundOption } from "../../../context/BackgroundContext";
import classes from "../Settings.module.scss";

// Predefined color themes using colors from colors.scss
const colorThemes = [
    { name: "Gold", color: "#9f8f5e" }, // primary-500
    { name: "Blue", color: "#233dff" }, // secondary-500
    { name: "Gray", color: "#667085" }, // gray-500
    { name: "Green", color: "#12b76a" }, // success-500
    { name: "Orange", color: "#f79009" }, // warning-500
];

const AppearanceTab = () => {
    const { isDarkMode, toggleDarkMode, primaryColor, setPrimaryColor } = useTheme();
    const { selectedBackgroundId, setSelectedBackgroundId, availableBackgrounds } = useBackground();

    // Handle feedback button click
    const handleFeedbackClick = () => {
        const googleFormUrl =
            "https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog";
        window.open(googleFormUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className={classes.tabContent}>
            <Typography variant="h5" className={classes.tabTitle}>
                Appearance Settings
            </Typography>
            <Divider className={classes.divider} />

            {/* Theme Settings Section */}
            <div className={classes.settingSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                    Theme
                </Typography>
                <FormControlLabel
                    control={
                        <Switch checked={isDarkMode} onChange={toggleDarkMode} color="primary" />
                    }
                    label={isDarkMode ? "Dark Mode" : "Light Mode"}
                    className={classes.themeToggleControl}
                />
                <Typography variant="body2" className={classes.settingDescription}>
                    Choose between light and dark theme for the application.
                </Typography>
            </div>

            {/* Color Theme Section */}
            <div className={classes.settingSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                    Color Theme
                </Typography>
                <div className={classes.colorThemeGrid}>
                    {colorThemes.map((theme) => (
                        <div
                            key={theme.color}
                            className={`${classes.colorThemeOption} ${
                                primaryColor === theme.color ? classes.activeColor : ""
                            }`}
                            onClick={() => setPrimaryColor(theme.color)}
                            style={{
                                backgroundColor: theme.color,
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === "Enter" && setPrimaryColor(theme.color)}
                            aria-label={`Select ${theme.name} theme`}
                        >
                            <span className={classes.colorName}>{theme.name}</span>
                        </div>
                    ))}
                </div>
                <Typography variant="body2" className={classes.settingDescription}>
                    <br />
                    Select a color theme to customize the application&apos;s primary colors.
                </Typography>
            </div>

            {/* Background Design Section */}
            <div className={classes.settingSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                    Background Design
                </Typography>
                <div className={classes.backgroundOptionsGrid}>
                    {availableBackgrounds.map((option: BackgroundOption) => (
                        <div
                            key={option.id}
                            className={`${classes.backgroundOptionCard} ${
                                selectedBackgroundId === option.id ? classes.activeBackground : ""
                            }`}
                            onClick={() => setSelectedBackgroundId(option.id)}
                        >
                            <div
                                className={classes.backgroundPreview}
                                style={{
                                    backgroundImage: isDarkMode
                                        ? option.previewDarkSvg
                                        : option.previewLightSvg,
                                }}
                            >
                                {/* Preview text removed to show background */}
                            </div>
                            <Typography variant="subtitle2" className={classes.backgroundName}>
                                {option.name}
                            </Typography>
                        </div>
                    ))}
                </div>
                <Typography variant="body2" className={classes.settingDescription}>
                    Select a global background design for the application.
                </Typography>
            </div>

            {/* Feedback Button */}
            <Box
                className={classes.feedbackButton}
                onClick={handleFeedbackClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleFeedbackClick()}
                aria-label="Share feedback about MAX Aspire"
            >
                <FeedbackOutlined className={classes.feedbackIcon} />
                <Typography variant="body2" className={classes.feedbackText}>
                    Help us make Aspire better
                </Typography>
            </Box>
        </div>
    );
};

export default AppearanceTab;
